import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/pookieApi";
import { Loader2, Crown, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function PaySuccess() {
  const [params] = useSearchParams();
  const order_id = params.get("order_id") || params.get("session_id");
  const [state, setState] = useState({ status: "polling", info: null });
  const attempts = useRef(0);
  const nav = useNavigate();

  useEffect(() => {
    if (!order_id) {
      setState({ status: "error", info: "Missing order id." });
      return;
    }
    let cancelled = false;

    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${order_id}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid", info: data });
          return;
        }
        if (attempts.current >= 8) {
          setState({ status: "timeout", info: data });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (cancelled) return;
        if (attempts.current >= 5) {
          setState({ status: "error", info: e?.response?.data?.detail || "Could not verify payment." });
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();

    return () => {
      cancelled = true;
    };
  }, [order_id]);

  return (
    <div className="max-w-2xl mx-auto px-5 md:px-8 py-16" data-testid="pay-success-page">
      {state.status === "polling" && (
        <div className="pl-card bg-white p-10 text-center">
          <Loader2 className="mx-auto animate-spin mb-4" size={40} strokeWidth={3} />
          <h1 className="font-display text-3xl">Confirming your payment…</h1>
          <p className="font-mono-pl text-sm mt-2 text-black/70">
            The clerk is stamping your subscription. One sec, pookie.
          </p>
        </div>
      )}
      {state.status === "paid" && (
        <div className="pl-card bg-[#B4F8C8] p-10 text-center" data-testid="pay-success-confirmed">
          <CheckCircle2 className="mx-auto mb-4 text-emerald-700" size={48} strokeWidth={3} />
          <div className="pl-chip bg-[#FFE600] mx-auto mb-3 inline-flex">
            <Crown size={14} strokeWidth={3} /> Pookie Pro activated
          </div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter">You're official, pookie.</h1>
          <p className="font-mono-pl text-sm md:text-base mt-3 text-black/80">
            Unlimited verdicts unlocked. Go ruin some reputations (lovingly).
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <button
              onClick={() => nav("/file")}
              className="pl-btn pl-btn-primary"
              data-testid="pay-success-file"
            >
              File your next case
            </button>
            <Link to="/" className="pl-btn pl-btn-ghost">Back to court</Link>
          </div>
        </div>
      )}
      {(state.status === "expired" || state.status === "timeout") && (
        <div className="pl-card bg-[#FFAEBC] p-10 text-center">
          <AlertTriangle className="mx-auto mb-4" size={40} strokeWidth={3} />
          <h1 className="font-display text-3xl">
            {state.status === "expired" ? "Session expired" : "Still confirming…"}
          </h1>
          <p className="font-mono-pl text-sm mt-2">
            If you completed payment, refresh in a moment. Otherwise try again.
          </p>
          <Link to="/pricing" className="pl-btn pl-btn-secondary mt-5 inline-flex">Back to pricing</Link>
        </div>
      )}
      {state.status === "error" && (
        <div className="pl-card bg-white p-10 text-center">
          <AlertTriangle className="mx-auto mb-4" size={40} strokeWidth={3} />
          <h1 className="font-display text-3xl">Something went sideways.</h1>
          <p className="font-mono-pl text-sm mt-2 text-black/70">{state.info}</p>
          <Link to="/pricing" className="pl-btn pl-btn-secondary mt-5 inline-flex">Try again</Link>
        </div>
      )}
    </div>
  );
}
